import { useState, useEffect, useRef, useMemo } from "react";
import { stockReceiptApi, stockReceiptDtlApi, vendorApi, productApi, inventoryItemDtlApi, productCategoryApi, inventorySummaryApi, barcodeApi } from "../api/api";
import { emitDataMutation } from "../utils/appEvents";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../components/Pagination";
import SearchableDropdown from "../components/SearchableDropdown";
import MasterDetailView from "../components/MasterDetailView";
import RvDataCard, { RvDataCardList } from "../components/RvDataCard";
import BarcodeLabelPrinter from "./BarcodeLabelPrinter";
import LineItemsEditor from "../components/LineItemsEditor";

/* ── Toast ── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ── Confirm Dialog ── */
function ConfirmDialog({ name, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop" style={{ zIndex: 500 }}>
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><TrashIcon /></div>
        <h3 className="rv-dialog-title">Delete Stock Receipt?</h3>
        <p className="rv-dialog-desc">
          <strong>{name}</strong> will be permanently removed. This action cannot be undone.
        </p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ── Stock Receipt Form Modal ── */
function StockReceiptModal({ initial, vendors, products, onSave, onClose }) {
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.receiptID;
  const todayISO = new Date().toLocaleDateString("en-CA");

  const [categories, setCategories] = useState([]);
  const [autoTag, setAutoTag] = useState(false);
  useEffect(() => {
    productCategoryApi.getAll()
      .then(res => {
        const list = Array.isArray(res) ? res : res?.data ?? [];
        setCategories(list);
      })
      .catch(() => {
        setCategories([]);
      });
  }, []);

  const [form, setForm] = useState({
    vendorID: initial?.vendorID ? String(initial.vendorID) : "",
    receiptDate: initial?.receiptDate ? initial.receiptDate.split("T")[0] : todayISO,
    comments: initial?.comments ?? "",
  });

  const [lines, setLines] = useState(() =>
    initial?.lines && initial.lines.length > 0
      ? initial.lines.map(l => {
          const prod = products.find(p => Number(p.productID) === Number(l.productID));
          const cat = prod?.categoryID ? String(prod.categoryID) : "";
          return {
            receiptLineID: l.receiptLineID ?? null,
            lineCategory: cat,
            categoryID: cat,
            productID: l.productID != null ? String(l.productID) : "",
            quantity: l.quantity != null ? String(l.quantity) : "",
            unitWeight: l.unitWeight != null ? String(l.unitWeight) : "",
            totalWeight: l.totalWeight != null ? String(l.totalWeight) : "",
            comments: l.comments ?? "",
          };
        })
      : []
  );

  const [errors, setErrors] = useState({});

  /* ── Line Entry Form State ── */
  const blankLineForm = () => ({
    receiptLineID: null,
    lineCategory: "",
    categoryID: "",
    productID: "",
    quantity: 1,
    unitWeight: "",
    totalWeight: "",
    comments: "",
  });

  const [lineForm, setLineForm] = useState(blankLineForm);
  const [editingIndex, setEditingIndex] = useState(null);
  const [lineFormErrors, setLineFormErrors] = useState({});

  const handleLineCategoryChange = (catVal) => {
    setLineForm(prev => {
      let nextProductID = prev.productID;
      if (catVal && nextProductID) {
        const p = products.find(prod => String(prod.productID) === String(nextProductID));
        if (p && String(p.categoryID ?? p.categoryId) !== String(catVal)) {
          nextProductID = "";
        }
      }
      return { ...prev, lineCategory: catVal, categoryID: catVal, productID: nextProductID };
    });
  };

  const handleVendorChange = (vendorVal) => {
    setForm(f => ({ ...f, vendorID: vendorVal }));
    setErrors(v => ({ ...v, vendorID: "" }));
    setEditingIndex(null);
    setLineForm(prev => ({
      ...prev,
      productID: "",
      lineCategory: "",
      categoryID: "",
    }));
    setLineFormErrors(prev => ({ ...prev, productID: "" }));
  };

  const handleLineProductChange = (prodVal) => {
    const p = products.find(prod => String(prod.productID) === String(prodVal));
    const cat = (!lineForm.lineCategory && !lineForm.categoryID && p?.categoryID)
      ? String(p.categoryID)
      : (lineForm.lineCategory || lineForm.categoryID || "");
    setLineForm(prev => ({ ...prev, productID: prodVal, lineCategory: cat, categoryID: cat }));
    setLineFormErrors(prev => ({ ...prev, productID: "" }));
  };

  const updateLineFormField = (key, val) => {
    setLineForm(prev => {
      const next = { ...prev, [key]: val };
      if (key === "quantity") {
        const q = Number(val) || 0;
        const uw = parseFloat(prev.unitWeight) || 0;
        if (q > 0 && uw > 0) {
          next.totalWeight = (q * uw).toFixed(3);
        } else if (q > 0 && parseFloat(prev.totalWeight) > 0) {
          next.unitWeight = (parseFloat(prev.totalWeight) / q).toFixed(3);
        }
      } else if (key === "unitWeight") {
        const uw = parseFloat(val) || 0;
        const q = Number(prev.quantity) || 1;
        if (uw >= 0) {
          next.totalWeight = (q * uw).toFixed(3);
        }
      } else if (key === "totalWeight") {
        const tw = parseFloat(val) || 0;
        const q = Number(prev.quantity) || 1;
        if (tw >= 0 && q > 0) {
          next.unitWeight = (tw / q).toFixed(3);
        }
      }
      return next;
    });
    setLineFormErrors(prev => ({ ...prev, [key]: "" }));
  };

  const handleSaveLineForm = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const errs = {};
    if (!lineForm.productID) errs.productID = "Select a product";
    if (!lineForm.quantity || isNaN(Number(lineForm.quantity)) || Number(lineForm.quantity) <= 0) {
      errs.quantity = "Qty must be > 0";
    }
    if (lineForm.unitWeight !== "" && lineForm.unitWeight != null) {
      if (isNaN(Number(lineForm.unitWeight)) || Number(lineForm.unitWeight) < 0) {
        errs.unitWeight = "Unit weight cannot be negative.";
      }
    }
    if (lineForm.totalWeight !== "" && lineForm.totalWeight != null) {
      if (isNaN(Number(lineForm.totalWeight)) || Number(lineForm.totalWeight) < 0) {
        errs.totalWeight = "Total weight cannot be negative.";
      }
    }

    if (Object.keys(errs).length > 0) {
      setLineFormErrors(errs);
      return;
    }

    const cleanLine = {
      ...lineForm,
      quantity: Number(lineForm.quantity),
      unitWeight: parseFloat(lineForm.unitWeight) || 0,
      totalWeight: parseFloat(lineForm.totalWeight) || 0,
    };

    if (editingIndex !== null) {
      setLines(prev => prev.map((l, i) => i === editingIndex ? cleanLine : l));
      setEditingIndex(null);
    } else {
      setLines(prev => [...prev, cleanLine]);
    }

    setLineForm(blankLineForm());
    setLineFormErrors({});
    setErrors(prev => ({ ...prev, lines: "" }));
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setLineForm(blankLineForm());
    setLineFormErrors({});
  };

  const handleClearLineForm = () => {
    setLineForm(blankLineForm());
    setLineFormErrors({});
    setErrors(prev => ({ ...prev, lines: "" }));
  };

  const handleEditLine = (idx) => {
    const target = lines[idx];
    if (!target) return;
    setEditingIndex(idx);
    setLineForm({ ...target });
    setLineFormErrors({});
  };

  const handleDeleteLine = (idx) => {
    if (editingIndex === idx) {
      handleCancelEdit();
    } else if (editingIndex !== null && editingIndex > idx) {
      setEditingIndex(editingIndex - 1);
    }
    setLines(prev => prev.filter((_, i) => i !== idx));
  };

  const selectedProduct = lineForm.productID
    ? products.find(p => String(p.productID) === String(lineForm.productID))
    : null;

  const vendorCategories = useMemo(() => {
    if (!form.vendorID) return [];
    const vendorProductIDs = products
      .filter(p => {
        const vId = p?.vendorID ?? p?.VendorID;
        return vId != null && Number(vId) === Number(form.vendorID);
      })
      .map(p => Number(p.categoryID ?? p.categoryId))
      .filter(Boolean);

    return categories.filter(c =>
      vendorProductIDs.includes(Number(c.categoryID ?? c.id))
    );
  }, [products, categories, form.vendorID]);

  const categoryOptions = useMemo(() => {
    if (!form.vendorID) return [];
    return [
      { id: "", name: "All Categories" },
      ...vendorCategories.map(c => ({
        id: String(c.categoryID ?? c.id),
        name: c.categoryName ?? c.name,
      })),
    ];
  }, [form.vendorID, vendorCategories]);

  const getVendorProducts = () => {
    if (!form.vendorID) return [];
    return products.filter(p => {
      const vId = p?.vendorID ?? p?.VendorID;
      return vId != null && String(vId) === String(form.vendorID);
    });
  };

  const getFilteredProducts = () => {
    const vProds = getVendorProducts();
    const selectedCat = lineForm.lineCategory || lineForm.categoryID;
    if (!selectedCat) return vProds;
    return vProds.filter(p => String(p.categoryID ?? p.categoryId) === String(selectedCat));
  };

  const validate = () => {
    const e = {};
    if (!form.vendorID) e.vendorID = "Vendor is required.";
    if (!form.receiptDate) e.receiptDate = "Receipt date is required.";

    if (editingIndex !== null) {
      e.lines = "You have an uncommitted line edit. Click 'Update Line' or 'Cancel Edit' before saving.";
    } else if (lines.length === 0) {
      if (lineForm.productID) {
        e.lines = "Click '+ Add Line to Delivery' to add your entered line first, or 'Clear' to reset.";
      } else {
        e.lines = "At least one consignment line item is required.";
      }
    }
    return Object.keys(e).length === 0 ? null : e;
  };

  const handleSubmit = async () => {
    if (saving) return;
    const e = validate();
    if (e) { setErrors(e); return; }
    setSaving(true);
    try {
      // Category value is NOT sent in the API payload - it's a UI filter only
      const cleanLines = lines.map(({ categoryID, lineCategory, ...rest }) => rest);
      await onSave({ ...form, lines: cleanLines, autoTag });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form" style={{ maxWidth: 860, width: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><ReceiptIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Delivery Record" : "Record Supplier Delivery"}</h3>
            <p className="rv-modal-sub">{isEdit ? "Update receipt details." : "Record a new vendor delivery."}</p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body" style={{ overflowY: "auto", flex: "1 1 auto", minHeight: 0, paddingBottom: 24 }}>

          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Vendor <span className="rv-required">*</span></label>
              <SearchableDropdown
                options={vendors}
                valueKey="vendorID"
                labelKey="vendorName"
                value={form.vendorID}
                onChange={handleVendorChange}
                placeholder="— Select vendor —"
                searchPlaceholder="Search vendors…"
                error={errors.vendorID}
                pageSize={10}
              />
              {errors.vendorID && <p className="rv-field-err">{errors.vendorID}</p>}
            </div>
            <div className="rv-field">
              <label className="rv-label">Receipt Date <span className="rv-required">*</span></label>
              <input
                className={`rv-input rv-input--date ${errors.receiptDate ? "rv-input--err" : ""}`}
                type="date"
                value={form.receiptDate}
                onChange={e => { setForm(f => ({ ...f, receiptDate: e.target.value })); setErrors(v => ({ ...v, receiptDate: "" })); }}
              />
              {errors.receiptDate && <p className="rv-field-err">{errors.receiptDate}</p>}
            </div>
          </div>

          <div className="rv-field">
            <label className="rv-label">Delivery Notes</label>
            <textarea
              className="rv-input"
              rows={2}
              placeholder="Optional notes…"
              value={form.comments}
              maxLength={500}
              onChange={e => setForm(f => ({ ...f, comments: e.target.value }))}
              style={{ resize: "vertical" }}
            />

            <p style={{ fontSize: 11, color: "var(--taupe)", textAlign: "right", marginTop: 2 }}>
              {form.comments.length}/500
            </p>
          </div>

          <LineItemsEditor
            title="Consignment Line Items"
            required
            items={lines}
            editingIndex={editingIndex}
            editingTitle={`Editing Line #${editingIndex !== null ? editingIndex + 1 : ""}`}
            addTitle="Add Consignment Line"
            error={errors.lines}
            onEdit={handleEditLine}
            onDelete={handleDeleteLine}
            onCancelEdit={handleCancelEdit}
            onClear={handleClearLineForm}
            emptyTitle="No consignment lines added yet"
            emptyMessage="Select a product above and click '+ Add Line to Delivery'."
            renderForm={({ isEditing }) => (
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 }}>
                  {/* Category filter */}
                  <div className="rv-field" style={{ marginBottom: 0 }}>
                    <label className="rv-label" style={{ fontSize: 11.5 }}>
                      Category <span style={{ fontSize: 10.5, fontWeight: 400, color: "var(--taupe)" }}>(Filter)</span>
                    </label>
                    <SearchableDropdown
                      options={categoryOptions}
                      valueKey="id"
                      labelKey="name"
                      value={lineForm.lineCategory || lineForm.categoryID || ""}
                      onChange={handleLineCategoryChange}
                      disabled={!form.vendorID}
                      placeholder={!form.vendorID ? "— Select a vendor first —" : "All Categories"}
                      searchPlaceholder="Search categories…"
                      pageSize={10}
                    />
                  </div>

                  {/* Product */}
                  <div className="rv-field" style={{ marginBottom: 0 }}>
                    <label className="rv-label" style={{ fontSize: 11.5 }}>
                      Product <span className="rv-required">*</span>
                    </label>
                    <SearchableDropdown
                      options={getFilteredProducts()}
                      valueKey="productID"
                      labelKey="productName"
                      value={lineForm.productID}
                      onChange={handleLineProductChange}
                      disabled={!form.vendorID || getVendorProducts().length === 0}
                      placeholder={
                        !form.vendorID
                          ? "— Select a vendor first —"
                          : getVendorProducts().length === 0
                          ? "No products found for this vendor"
                          : "— Select product —"
                      }
                      searchPlaceholder="Search products…"
                      error={lineFormErrors.productID}
                      pageSize={10}
                    />
                    {Boolean(lineForm.productID && selectedProduct) && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginTop: 6,
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            background: "rgba(160,120,80,0.08)",
                            border: "1px solid rgba(160,120,80,0.2)",
                            color: "#A07850",
                            padding: "3px 10px",
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 600,
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          ℹ Pricing Type: {selectedProduct.pricingType || selectedProduct.PricingType || "Fixed"}
                        </span>
                        {(selectedProduct.purityStandard || selectedProduct.purity) && (
                          <span
                            style={{
                              background: "rgba(160,120,80,0.08)",
                              border: "1px solid rgba(160,120,80,0.2)",
                              color: "#A07850",
                              padding: "3px 10px",
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 600,
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            ✦ Purity: {selectedProduct.purityStandard || selectedProduct.purity}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Numbers row: Quantity, Unit Weight, Total Weight */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 8 }}>
                  <div className="rv-field" style={{ marginBottom: 0 }}>
                    <label className="rv-label" style={{ fontSize: 11.5 }}>
                      Qty Received <span className="rv-required">*</span>
                    </label>
                    <input
                      className={`rv-input ${lineFormErrors.quantity ? "rv-input--err" : ""}`}
                      type="number"
                      min="1"
                      placeholder="0"
                      value={lineForm.quantity}
                      onChange={e => updateLineFormField("quantity", e.target.value)}
                    />
                    {lineFormErrors.quantity && <p className="rv-field-err">{lineFormErrors.quantity}</p>}
                  </div>

                  <div className="rv-field" style={{ marginBottom: 0 }}>
                    <label className="rv-label" style={{ fontSize: 11.5 }}>Unit Weight (g)</label>
                    <input
                      className="rv-input"
                      type="number"
                      min="0"
                      step="0.001"
                      placeholder="0.000"
                      value={lineForm.unitWeight}
                      onChange={e => updateLineFormField("unitWeight", e.target.value)}
                    />
                  </div>

                  <div className="rv-field" style={{ marginBottom: 0 }}>
                    <label className="rv-label" style={{ fontSize: 11.5 }}>Total Weight (g)</label>
                    <input
                      className="rv-input"
                      type="number"
                      min="0"
                      step="0.001"
                      placeholder="0.000"
                      value={lineForm.totalWeight}
                      onChange={e => updateLineFormField("totalWeight", e.target.value)}
                    />
                  </div>
                </div>

                {/* Comments + Add/Update Button */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "flex-end" }}>
                  <div className="rv-field" style={{ marginBottom: 0 }}>
                    <label className="rv-label" style={{ fontSize: 11.5 }}>Line Comments</label>
                    <input
                      className="rv-input"
                      type="text"
                      placeholder="Optional notes for this item…"
                      value={lineForm.comments}
                      onChange={e => updateLineFormField("comments", e.target.value)}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {isEditing ? (
                      <button
                        type="button"
                        className="rv-btn rv-btn--ghost"
                        onClick={handleCancelEdit}
                        style={{ height: 38, padding: "0 14px", fontSize: 12 }}
                      >
                        Cancel Edit
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="rv-btn rv-btn--ghost"
                        onClick={handleClearLineForm}
                        style={{ height: 38, padding: "0 14px", fontSize: 12 }}
                        title="Clear form inputs"
                      >
                        Clear
                      </button>
                    )}
                    <button
                      type="button"
                      className="rv-btn rv-btn--primary"
                      onClick={handleSaveLineForm}
                      style={{
                        height: 38,
                        padding: "0 16px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {isEditing ? "Update Line" : "+ Add Line to Delivery"}
                    </button>
                  </div>
                </div>
              </div>
            )}
            columns={[
              {
                header: "Product",
                render: (line) => {
                  const p = products.find(x => String(x.productID) === String(line.productID));
                  return (
                    <div>
                      <div style={{ fontWeight: 600, color: "var(--dark)" }}>
                        {p?.productName || line.productName || `Product #${line.productID}`}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--taupe)" }}>
                        {p?.categoryName || p?.productCategory || ""}
                        {p?.productCode ? ` • ${p.productCode}` : ""}
                      </div>
                    </div>
                  );
                }
              },
              {
                header: "Qty",
                align: "right",
                width: 60,
                render: (l) => <strong>{l.quantity}</strong>
              },
              {
                header: "Unit Wt (g)",
                align: "right",
                width: 100,
                render: (l) => l.unitWeight ? `${parseFloat(l.unitWeight).toFixed(3)}g` : "—"
              },
              {
                header: "Total Wt (g)",
                align: "right",
                width: 110,
                render: (l) => <strong>{parseFloat(l.totalWeight || 0).toFixed(3)}g</strong>
              },
              {
                header: "Comments",
                render: (l) => l.comments ? <span style={{ color: "var(--taupe-dark)" }}>{l.comments}</span> : "—"
              }
            ]}
            renderTotals={(items) => {
              const totalQty = items.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);
              const totalWt = items.reduce((sum, l) => sum + (parseFloat(l.totalWeight) || 0), 0);
              return (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 20,
                    marginTop: 8,
                    padding: "8px 12px",
                    background: "rgba(185,167,146,.08)",
                    borderRadius: 8,
                    fontSize: 12.5,
                  }}
                >
                  <div>
                    <span style={{ color: "var(--taupe)", marginRight: 6 }}>Total Lines:</span>
                    <strong>{items.length}</strong>
                  </div>
                  <div>
                    <span style={{ color: "var(--taupe)", marginRight: 6 }}>Total Pieces:</span>
                    <strong>{totalQty}</strong>
                   </div>
                  <div>
                    <span style={{ color: "var(--taupe)", marginRight: 6 }}>Total Weight:</span>
                    <strong style={{ color: "var(--dark)" }}>{totalWt.toFixed(3)} g</strong>
                  </div>
                </div>
              );
            }}
          />

          {/* Equal-Weight Auto-Tag Toggle */}
          {!isEdit && (
            <div
              className="rv-field rv-field--row"
              style={{
                marginTop: 16,
                padding: "12px 16px",
                background: autoTag ? "rgba(104,26,45,.06)" : "rgba(185,167,146,.08)",
                border: autoTag ? "1px solid rgba(104,26,45,.22)" : "1px solid rgba(185,167,146,.25)",
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
              }}
            >
              <div>
                <label
                  className="rv-label"
                  style={{
                    marginBottom: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    color: autoTag ? "var(--burgundy-dark)" : "var(--dark)",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                  onClick={() => setAutoTag(!autoTag)}
                >
                  <SparklesIcon /> Equal-Weight Auto-Tagging
                </label>
                <p className="rv-field-hint" style={{ margin: 0, fontSize: 11.5 }}>
                  Turn ON if all pieces in this delivery have standard equal weights to auto-create barcode tags and open label printer immediately on save.
                </p>
              </div>
              <button
                type="button"
                className={`rv-toggle ${autoTag ? "rv-toggle--on" : ""}`}
                onClick={() => setAutoTag(!autoTag)}
                title="Toggle equal-weight auto-tagging on save"
                style={{ flexShrink: 0 }}
              >
                <span className="rv-toggle-knob" />
              </button>
            </div>
          )}

        </div>

        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving && <span className="rv-spinner" />} {isEdit ? "Update" : "Save Receipt"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Tag Generation Modal (Option 1: Individual Piece Tags) ── */
function TagGenerationModal({ line, product, receipt, vendorName, onSave, onClose, saving }) {
  const lineQty = Math.max(1, Number(line.quantity) || 1);
  const totalLineWeight = parseFloat(line.totalWeight) || 0;
  const initialUnitGross = line.unitWeight
    ? parseFloat(line.unitWeight)
    : totalLineWeight > 0
    ? parseFloat((totalLineWeight / lineQty).toFixed(3))
    : 0;

  // For multi-piece lines: "equal" (distribute evenly) vs "custom" (per-piece weights)
  const [mode, setMode] = useState("equal");

  // Single / Equal form state
  const [singleForm, setSingleForm] = useState({
    grossWeight: initialUnitGross ? String(initialUnitGross) : "",
    lessWeight: "0",
    netWeight: initialUnitGross ? String(initialUnitGross) : "",
  });

  // Custom multi-piece weights array
  const [pieces, setPieces] = useState(() =>
    Array.from({ length: lineQty }, (_, idx) => ({
      index: idx + 1,
      grossWeight: initialUnitGross ? String(initialUnitGross) : "",
      lessWeight: "0",
      netWeight: initialUnitGross ? String(initialUnitGross) : "",
    }))
  );

  const [errors, setErrors] = useState({});

  const updateSingleField = (key, val) => {
    setSingleForm(prev => {
      const updated = { ...prev, [key]: val };
      if (key === "grossWeight" || key === "lessWeight") {
        const g = parseFloat(key === "grossWeight" ? val : updated.grossWeight) || 0;
        const l = parseFloat(key === "lessWeight" ? val : updated.lessWeight) || 0;
        updated.netWeight = Math.max(0, parseFloat((g - l).toFixed(3)));
      }
      return updated;
    });
    setErrors(e => ({ ...e, [key]: "" }));
  };

  const updatePieceRow = (idx, key, val) => {
    setPieces(prev => {
      const next = [...prev];
      const cur = { ...next[idx], [key]: val };
      if (key === "grossWeight" || key === "lessWeight") {
        const g = parseFloat(key === "grossWeight" ? val : cur.grossWeight) || 0;
        const l = parseFloat(key === "lessWeight" ? val : cur.lessWeight) || 0;
        cur.netWeight = Math.max(0, parseFloat((g - l).toFixed(3)));
      }
      next[idx] = cur;
      return next;
    });
  };

  const applyEqualWeightToAll = () => {
    const unitGross = parseFloat((totalLineWeight / lineQty).toFixed(3)) || 0;
    setPieces(prev =>
      prev.map(p => ({
        ...p,
        grossWeight: unitGross ? String(unitGross) : "",
        lessWeight: "0",
        netWeight: unitGross ? String(unitGross) : "",
      }))
    );
  };

  const sumGrossCustom = pieces.reduce((acc, p) => acc + (parseFloat(p.grossWeight) || 0), 0);
  const sumNetCustom   = pieces.reduce((acc, p) => acc + (parseFloat(p.netWeight) || 0), 0);
  const weightDifference = parseFloat((sumGrossCustom - totalLineWeight).toFixed(3));
  const isWeightMatched = Math.abs(weightDifference) < 0.005;

  const handleConfirm = () => {
    const errs = {};

    if (lineQty === 1 || mode === "equal") {
      const g = parseFloat(singleForm.grossWeight);
      const l = parseFloat(singleForm.lessWeight) || 0;
      if (!singleForm.grossWeight || isNaN(g) || g <= 0) {
        errs.grossWeight = "Valid gross weight is required.";
      } else if (l >= g) {
        errs.lessWeight = "Less weight cannot be greater than or equal to gross weight.";
      }
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        return;
      }

      const gVal = parseFloat(singleForm.grossWeight) || 0;
      const lVal = parseFloat(singleForm.lessWeight) || 0;
      const nVal = Math.max(0, parseFloat((gVal - lVal).toFixed(3)));

      const payload = Array.from({ length: lineQty }, () => ({
        receiptLineID: Number(line.receiptLineID),
        productID: Number(line.productID),
        grossWeight: gVal,
        lessWeight: lVal,
        netWeight: nVal,
        quantity: 1,
        barcodeID: 0,
      }));

      onSave(payload);
    } else {
      const invalidIdx = pieces.findIndex(p => !p.grossWeight || isNaN(parseFloat(p.grossWeight)) || parseFloat(p.grossWeight) <= 0);
      if (invalidIdx !== -1) {
        setErrors({ general: `Piece #${invalidIdx + 1} has an invalid gross weight.` });
        return;
      }
      const invalidWeightIdx = pieces.findIndex(p => {
        const gw = parseFloat(p.grossWeight);
        const lw = parseFloat(p.lessWeight) || 0;
        return lw >= gw;
      });
      if (invalidWeightIdx !== -1) {
        setErrors({ general: `Piece #${invalidWeightIdx + 1} has less weight greater than or equal to gross weight.` });
        return;
      }

      const payload = pieces.map(p => {
        const gVal = parseFloat(p.grossWeight) || 0;
        const lVal = parseFloat(p.lessWeight) || 0;
        const nVal = Math.max(0, parseFloat((gVal - lVal).toFixed(3)));
        return {
          receiptLineID: Number(line.receiptLineID),
          productID: Number(line.productID),
          grossWeight: gVal,
          lessWeight: lVal,
          netWeight: nVal,
          quantity: 1,
          barcodeID: 0,
        };
      });

      onSave(payload);
    }
  };

  return (
    <div className="rv-dialog-backdrop" style={{ zIndex: 10000 }}>
      <div className="rv-dialog rv-dialog--form" style={{ maxWidth: 680, width: "100%", maxHeight: "92vh", display: "flex", flexDirection: "column", borderRadius: 14 }}>
        {/* Header */}
        <div className="rv-modal-header" style={{ padding: "18px 24px", borderBottom: "1px solid rgba(185, 167, 146, .2)" }}>
          <div className="rv-modal-icon" style={{ background: "rgba(104, 26, 45, 0.1)", color: "var(--burgundy-dark)" }}>
            <TagIcon />
          </div>
          <div style={{ flex: "1 1 auto" }}>
            <h3 className="rv-modal-title" style={{ fontSize: 18, fontWeight: 700, color: "var(--dark)" }}>
              Generate Barcode Tags ({lineQty} {lineQty === 1 ? "Piece" : "Pieces"})
            </h3>
            <p className="rv-modal-sub" style={{ fontSize: 12.5, color: "var(--taupe)", margin: "2px 0 0" }}>
              {lineQty > 1
                ? `Assign piece weights and generate individual scannable barcode tags for all ${lineQty} pieces.`
                : "Assign piece weight and generate individual scannable Code128 barcode tag."}
            </p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        {/* Body */}
        <div className="rv-modal-body" style={{ overflowY: "auto", flex: "1 1 auto", padding: "20px 24px" }}>
          {/* Luxury Jewellery Item Summary Card */}
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 14,
            padding: "16px 20px",
            background: "linear-gradient(135deg, rgba(104,26,45,.06) 0%, rgba(185,167,146,.10) 100%)",
            borderRadius: 12,
            border: "1px solid rgba(104,26,45,.14)",
            marginBottom: 18,
          }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--burgundy)", textTransform: "uppercase", letterSpacing: ".5px" }}>
                Delivery Line Item · {lineQty} {lineQty === 1 ? "Piece" : "Pieces"}
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "var(--dark)", marginTop: 3 }}>
                {product?.productName || `Product #${line.productID}`}
              </div>
              <div style={{ fontSize: 12.5, color: "var(--taupe-dark)", marginTop: 4, display: "flex", gap: 12, flexWrap: "wrap" }}>
                <span>Vendor: <strong>{vendorName}</strong></span>
                <span>•</span>
                <span>Delivery Slip: <strong>#{receipt.receiptID}</strong></span>
                <span>•</span>
                <span>Line Total Wt: <strong>{totalLineWeight.toFixed(3)} g</strong></span>
              </div>
            </div>

            {product?.purityStandard && (
              <span style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#7a3f12",
                background: "rgba(217, 119, 6, 0.12)",
                padding: "6px 14px",
                borderRadius: 8,
                border: "1px solid rgba(217, 119, 6, 0.28)",
                letterSpacing: ".3px",
                whiteSpace: "nowrap"
              }}>
                ✦ {product.purityStandard}
              </span>
            )}
          </div>

          {errors.general && (
            <div style={{ color: "#c53030", background: "rgba(229,62,62,.1)", border: "1px solid rgba(229,62,62,.25)", padding: "10px 14px", borderRadius: 8, fontSize: 13, margin: "0 0 16px 0", display: "flex", alignItems: "center", gap: 8 }}>
              <AlertIcon /> {errors.general}
            </div>
          )}

          {/* Mode Switcher for Multi-Piece Lines */}
          {lineQty > 1 && (
            <div style={{ marginBottom: 18, background: "rgba(185,167,146,.12)", padding: 4, borderRadius: 10, display: "flex", gap: 6 }}>
              <button
                type="button"
                onClick={() => setMode("equal")}
                style={{
                  flex: 1, padding: "10px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                  fontWeight: 600, fontSize: 13,
                  background: mode === "equal" ? "#fff" : "transparent",
                  color: mode === "equal" ? "var(--burgundy-dark)" : "var(--taupe-dark)",
                  boxShadow: mode === "equal" ? "0 2px 6px rgba(0,0,0,.08)" : "none",
                  transition: "all .15s",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                <span>⚡</span>
                <span>Equal Split (<strong>{initialUnitGross.toFixed(3)}g</strong> each)</span>
              </button>
              <button
                type="button"
                onClick={() => setMode("custom")}
                style={{
                  flex: 1, padding: "10px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                  fontWeight: 600, fontSize: 13,
                  background: mode === "custom" ? "#fff" : "transparent",
                  color: mode === "custom" ? "var(--burgundy-dark)" : "var(--taupe-dark)",
                  boxShadow: mode === "custom" ? "0 2px 6px rgba(0,0,0,.08)" : "none",
                  transition: "all .15s",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                <span>⚖️</span>
                <span>Custom Piece Weights (<strong>{lineQty}</strong> pcs)</span>
              </button>
            </div>
          )}

          {/* Equal Distribution Form */}
          {(lineQty === 1 || mode === "equal") && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div className="rv-field">
                  <label className="rv-label" style={{ fontWeight: 600, fontSize: 12.5 }}>
                    {lineQty > 1 ? "Unit Gross Weight (g per piece)" : "Gross Weight (g)"} <span className="rv-required">*</span>
                  </label>
                  <input
                    className={`rv-input ${errors.grossWeight ? "rv-input--err" : ""}`}
                    style={{ height: 42, fontSize: 14 }}
                    type="number" step="0.001" min="0"
                    placeholder="0.000"
                    value={singleForm.grossWeight}
                    onChange={e => updateSingleField("grossWeight", e.target.value)}
                  />
                  {errors.grossWeight && <p className="rv-field-err">{errors.grossWeight}</p>}
                </div>

                <div className="rv-field">
                  <label className="rv-label" style={{ fontWeight: 600, fontSize: 12.5 }}>
                    Less Weight (g per piece)
                  </label>
                  <input
                    className="rv-input"
                    style={{ height: 42, fontSize: 14 }}
                    type="number" step="0.001" min="0"
                    placeholder="0.000"
                    value={singleForm.lessWeight}
                    onChange={e => updateSingleField("lessWeight", e.target.value)}
                  />
                  <p className="rv-field-hint" style={{ margin: "4px 0 0", fontSize: 11.5, color: "var(--taupe)" }}>
                    Stone, enamel, or wax deduction
                  </p>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div className="rv-field">
                  <label className="rv-label" style={{ fontWeight: 600, fontSize: 12.5 }}>
                    Calculated Net Weight (g per piece)
                  </label>
                  <div style={{
                    height: 42, display: "flex", alignItems: "center", padding: "0 14px",
                    background: "rgba(104,26,45,.05)", borderRadius: 8, border: "1px solid rgba(104,26,45,.15)",
                    fontSize: 15, fontWeight: 700, color: "var(--burgundy-dark)"
                  }}>
                    {singleForm.netWeight ? `${singleForm.netWeight} g` : "0.000 g"}
                  </div>
                </div>

                <div className="rv-field">
                  <label className="rv-label" style={{ fontWeight: 600, fontSize: 12.5 }}>
                    Barcode Tags to Generate
                  </label>
                  <div style={{
                    height: 42, display: "flex", alignItems: "center", padding: "0 14px",
                    background: "rgba(185,167,146,.08)", borderRadius: 8, border: "1px solid rgba(185,167,146,.25)",
                    fontSize: 14, fontWeight: 600, color: "var(--dark)"
                  }}>
                    {lineQty} individual scannable tags
                  </div>
                </div>
              </div>

              {lineQty > 1 && (
                <div style={{
                  padding: "12px 16px", borderRadius: 10,
                  background: "rgba(46,125,50,.07)", border: "1px solid rgba(46,125,50,.22)",
                  fontSize: 13, color: "#2e7d32", display: "flex", alignItems: "center", gap: 10
                }}>
                  <span style={{ fontSize: 16 }}>✓</span>
                  <span>
                    Batch Mode: Generates <strong>{lineQty} individual barcode stickers</strong> ({singleForm.netWeight || 0}g net each), immediately scannable at POS checkout.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Individual Custom Weights Form (Image 2 Redesign) */}
          {lineQty > 1 && mode === "custom" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Reconciliation Balance Bar */}
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                flexWrap: "wrap", gap: 10, padding: "12px 16px", borderRadius: 10,
                background: isWeightMatched ? "rgba(46,125,50,.06)" : "rgba(217,119,6,.08)",
                border: isWeightMatched ? "1px solid rgba(46,125,50,.2)" : "1px solid rgba(217,119,6,.25)",
                fontSize: 13
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                  <span>
                    Sum of Pieces: <strong style={{ color: "var(--burgundy-dark)", fontSize: 14 }}>{sumGrossCustom.toFixed(3)} g</strong>
                  </span>
                  <span style={{ color: "var(--taupe)" }}>•</span>
                  <span>
                    Delivery Slip Target: <strong>{totalLineWeight.toFixed(3)} g</strong>
                  </span>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{
                    fontSize: 11.5, fontWeight: 700, padding: "3px 10px", borderRadius: 6,
                    background: isWeightMatched ? "rgba(46,125,50,.14)" : "rgba(217,119,6,.14)",
                    color: isWeightMatched ? "#2e7d32" : "#b45309"
                  }}>
                    {isWeightMatched ? "✓ Exact Match" : `Diff: ${weightDifference > 0 ? "+" : ""}${weightDifference.toFixed(3)}g`}
                  </span>

                  <button
                    type="button"
                    onClick={applyEqualWeightToAll}
                    style={{
                      background: "white", border: "1px solid rgba(185,167,146,.35)",
                      borderRadius: 6, padding: "3px 10px", fontSize: 11.5, fontWeight: 600,
                      color: "var(--dark)", cursor: "pointer"
                    }}
                    title="Reset all pieces to equal split"
                  >
                    Auto-Fill Equal
                  </button>
                </div>
              </div>

              {/* Individual Pieces Table */}
              <div style={{ maxHeight: 310, overflowY: "auto", border: "1px solid rgba(185,167,146,.25)", borderRadius: 10, background: "white" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "rgba(185,167,146,.10)", color: "var(--taupe-dark)", textAlign: "left", borderBottom: "1.5px solid rgba(185,167,146,.25)", position: "sticky", top: 0, zIndex: 1 }}>
                      <th style={{ padding: "10px 14px", width: 70 }}>Piece</th>
                      <th style={{ padding: "10px 14px" }}>Gross Weight (g)</th>
                      <th style={{ padding: "10px 14px" }}>Less Wt (g)</th>
                      <th style={{ padding: "10px 14px", textAlign: "right" }}>Net Weight (g)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pieces.map((p, idx) => (
                      <tr key={p.index} style={{ borderBottom: "1px solid rgba(185,167,146,.12)", background: idx % 2 === 0 ? "white" : "rgba(250, 248, 245, 0.6)" }}>
                        <td style={{ padding: "8px 14px", fontWeight: 700, color: "var(--burgundy-dark)" }}>
                          #{p.index}
                        </td>
                        <td style={{ padding: "6px 14px" }}>
                          <input
                            type="number" step="0.001" min="0"
                            className="rv-input"
                            style={{ height: 34, padding: "0 10px", fontSize: 13, width: "130px", borderRadius: 6 }}
                            value={p.grossWeight}
                            onChange={e => updatePieceRow(idx, "grossWeight", e.target.value)}
                            placeholder="0.000"
                          />
                        </td>
                        <td style={{ padding: "6px 14px" }}>
                          <input
                            type="number" step="0.001" min="0"
                            className="rv-input"
                            style={{ height: 34, padding: "0 10px", fontSize: 13, width: "110px", borderRadius: 6 }}
                            value={p.lessWeight}
                            onChange={e => updatePieceRow(idx, "lessWeight", e.target.value)}
                            placeholder="0.000"
                          />
                        </td>
                        <td style={{ padding: "8px 14px", fontWeight: 700, color: "var(--dark)", textAlign: "right" }}>
                          {p.netWeight ? `${parseFloat(p.netWeight).toFixed(3)} g` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="rv-modal-footer" style={{ borderTop: "1px solid rgba(185,167,146,.2)", padding: "16px 24px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving} style={{ padding: "8px 18px", fontSize: 13 }}>
            Cancel
          </button>
          <button className="rv-btn rv-btn--primary" onClick={handleConfirm} disabled={saving} style={{ padding: "8px 22px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 8 }}>
            {saving ? <span className="rv-spinner" /> : <SparklesIcon />}
            {saving ? "Generating Tags…" : `Generate & Print ${lineQty} ${lineQty === 1 ? "Tag" : "Tags"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
/* ── Stock Receipt Master-Detail View ── */
function StockReceiptDetailView({
  receipt,
  vendors,
  products,
  allDetails,
  inventoryItems = [],
  onBack,
  onEdit,
  onPrintTag,
  onGenerateTag,
  onPrintBatch,
  onAutoTagAll,
}) {
  useEffect(() => {
    const el = document.querySelector(".rv-content");
    if (el) el.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [receipt.receiptID]);

  const vendor = vendors.find(v => v.vendorID === receipt.vendorID);
  const vendorName = vendor?.vendorName ?? `Vendor #${receipt.vendorID}`;
  const lines = allDetails.filter(d => d.receiptID === receipt.receiptID);

  const [selectedLineId, setSelectedLineId] = useState(lines[0]?.receiptLineID ?? 0);

  const fmtDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };
  const fmtW = (v) => (v != null && v !== "" && !isNaN(v)) ? `${parseFloat(v).toFixed(3)} g` : "—";

  const totalQty = lines.reduce((acc, l) => acc + (Number(l.quantity) || 0), 0);
  const totalWeight = lines.reduce((acc, l) => acc + (parseFloat(l.totalWeight) || 0), 0);

  const selectedLine = lines.find(l => (l.receiptLineID ?? 0) === selectedLineId) || lines[0];

  // Tag status calculations
  const receiptTaggedItems = inventoryItems.filter(i =>
    lines.some(l => Number(l.receiptLineID) === Number(i.receiptLineID))
  );
  const untaggedLines = lines.filter(l => {
    const count = inventoryItems.filter(i => Number(i.receiptLineID) === Number(l.receiptLineID)).length;
    return count < (Number(l.quantity) || 1);
  });

  return (
    <MasterDetailView
      title={`Stock Receipt #${receipt.receiptID}`}
      subtitle={`${vendorName} · ${fmtDate(receipt.receiptDate)}`}
      badgeText={`${lines.length} Line Item${lines.length === 1 ? "" : "s"} · ${receiptTaggedItems.length} Tagged Pieces`}
      onBack={onBack}
      backLabel="Back to Receipts"
      timelineTitle={`Line Items (${lines.length})`}
      items={lines}
      selectedItem={selectedLine}
      onSelectItem={(item) => setSelectedLineId(item.receiptLineID ?? 0)}
      extraHeaderActions={
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {receiptTaggedItems.length > 0 && (
            <button
              className="rv-action-btn--print-tag"
              style={{ padding: "8px 14px", fontSize: 13 }}
              onClick={() => onPrintBatch(receiptTaggedItems)}
              title="Print jewelry tags for all tagged items in this receipt"
            >
              <PrintIcon /> Print All Tags ({receiptTaggedItems.length})
            </button>
          )}
          {untaggedLines.length > 0 && (
            <button
              className="rv-action-btn--generate-tag"
              style={{ padding: "8px 14px", fontSize: 13 }}
              onClick={() => onAutoTagAll(lines, receipt)}
              title="Auto-create individual inventory item tags for untagged pieces"
            >
              <SparklesIcon /> Auto-Tag All Pieces ({untaggedLines.length} Lines)
            </button>
          )}
          {/* <button
            className="rv-btn rv-btn--primary"
            style={{ padding: "8px 16px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}
            onClick={() => onEdit(receipt)}
          >
            <EditIcon /> Edit Receipt
          </button> */}
        </div>
      }
      renderTimelineItem={(line, idx, isSelected) => {
        const prod = products.find(p => p.productID === line.productID);
        const lineTaggedItems = inventoryItems.filter(i => Number(i.receiptLineID) === Number(line.receiptLineID));
        const lineQty = Math.max(1, Number(line.quantity) || 1);
        const taggedCount = lineTaggedItems.length;
        const isFullyTagged = taggedCount >= lineQty;

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: isSelected ? "var(--burgundy-dark)" : "var(--dark)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {prod?.productName ?? `Product #${line.productID}`}
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--taupe)", background: "rgba(185,167,146,.15)", padding: "2px 6px", borderRadius: 4 }}>
                #{idx + 1}
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, color: "var(--taupe)" }}>
              <span>Qty: <strong style={{ color: "var(--dark)" }}>{line.quantity ?? "—"}</strong></span>
              <span>Total Wt: <strong style={{ color: "var(--dark)" }}>{fmtW(line.totalWeight)}</strong></span>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginTop: 2 }}>
              {isFullyTagged ? (
                <span style={{ fontSize: 11, fontWeight: 700, color: "#2e7d32", display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <CheckIcon /> All {taggedCount} Pcs Tagged
                </span>
              ) : taggedCount > 0 ? (
                <span style={{ fontSize: 11, fontWeight: 600, color: "#b45309" }}>
                  ⚡ {taggedCount}/{lineQty} Pcs Tagged
                </span>
              ) : (
                <span style={{ fontSize: 11, fontWeight: 600, color: "#e65100" }}>
                  ⏳ 0/{lineQty} Tagged
                </span>
              )}
            </div>

            {line.comments && (
              <p style={{ margin: 0, fontSize: 11.5, color: "var(--taupe-dark)", fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                "{line.comments}"
              </p>
            )}
          </div>
        );
      }}
      renderDetail={(line) => {
        if (!line) {
          return (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--taupe)" }}>
              <p>No line items found for this receipt.</p>
            </div>
          );
        }

        const lineProd = products.find(p => p.productID === line.productID);
        const lineTaggedItems = inventoryItems.filter(i => Number(i.receiptLineID) === Number(line.receiptLineID));
        const lineQty = Math.max(1, Number(line.quantity) || 1);
        const taggedCount = lineTaggedItems.length;
        const isFullyTagged = taggedCount >= lineQty;

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Header summary banner */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 14,
              padding: "16px 20px",
              background: "linear-gradient(135deg, rgba(104,26,45,.06) 0%, rgba(185,167,146,.08) 100%)",
              borderRadius: 12,
              border: "1px solid rgba(104,26,45,.12)"
            }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", color: "var(--burgundy)" }}>
                  Product Details
                </div>
                <h3 style={{ margin: "3px 0 0 0", fontSize: 20, color: "var(--burgundy-dark)", fontWeight: 700 }}>
                  {lineProd?.productName ?? `Product #${line.productID}`}
                </h3>
                {lineProd?.productCode && (
                  <span style={{ fontSize: 12, color: "var(--taupe)", marginTop: 2, display: "inline-block" }}>
                    SKU / Code: <strong>{lineProd.productCode}</strong>
                  </span>
                )}
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div style={{ background: "white", padding: "8px 14px", borderRadius: 8, border: "1px solid rgba(185,167,146,.2)", textAlign: "center" }}>
                  <div style={{ fontSize: 10.5, textTransform: "uppercase", color: "var(--taupe)", fontWeight: 700 }}>Receipt Total Qty</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--burgundy)" }}>{totalQty} pcs</div>
                </div>
                <div style={{ background: "white", padding: "8px 14px", borderRadius: 8, border: "1px solid rgba(185,167,146,.2)", textAlign: "center" }}>
                  <div style={{ fontSize: 10.5, textTransform: "uppercase", color: "var(--taupe)", fontWeight: 700 }}>Receipt Total Wt</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--burgundy)" }}>{totalWeight.toFixed(3)} g</div>
                </div>
                <div style={{ background: "white", padding: "8px 14px", borderRadius: 8, border: "1px solid rgba(185,167,146,.2)", textAlign: "center" }}>
                  <div style={{ fontSize: 10.5, textTransform: "uppercase", color: "var(--taupe)", fontWeight: 700 }}>Barcode Status</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: isFullyTagged ? "#2e7d32" : taggedCount > 0 ? "#d97706" : "var(--taupe)", marginTop: 2 }}>
                    {isFullyTagged ? "✓ Fully Tagged" : taggedCount > 0 ? `⚡ Partially (${taggedCount}/${lineQty})` : "⏳ Untagged"}
                  </div>
                </div>
              </div>
            </div>

            {/* Jewelry Barcode Tag Actions */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 14,
              padding: "16px 20px",
              background: "rgba(185, 167, 146, 0.08)",
              border: "1px solid rgba(185, 167, 146, 0.25)",
              borderRadius: 12
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 10,
                  background: "rgba(104, 26, 45, 0.12)",
                  color: "var(--burgundy-dark)",
                  display: "flex", alignItems: "center", justifyContent: "center"
                }}>
                  <TagIcon />
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--dark)" }}>
                    Barcode Jewelry Tags
                  </h4>
                  <p style={{ margin: "3px 0 0 0", fontSize: 12.5, color: "var(--taupe)" }}>
                    {taggedCount > 0
                      ? `Generated ${taggedCount} of ${lineQty} pieces · Net Total Tagged: ${lineTaggedItems.reduce((acc, i) => acc + (parseFloat(i.netWeight) || 0), 0).toFixed(3)}g`
                      : `Generate individual Code128 string tags for each of the ${lineQty} pieces in this line.`
                    }
                  </p>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {taggedCount > 0 && (
                  <button
                    className="rv-action-btn--print-tag"
                    onClick={() => onPrintTag(lineTaggedItems, lineProd)}
                  >
                    <PrintIcon /> Print All Tags ({taggedCount})
                  </button>
                )}
                {!isFullyTagged && (
                  <button
                    className="rv-action-btn--generate-tag"
                    onClick={() => onGenerateTag(line, lineProd, receipt)}
                  >
                    <SparklesIcon /> {taggedCount > 0 ? `Tag Remaining (${lineQty - taggedCount})` : `Generate ${lineQty} ${lineQty === 1 ? "Tag" : "Tags"}`}
                  </button>
                )}
              </div>
            </div>

            {/* Individual Tagged Pieces Table for this line */}
            {lineTaggedItems.length > 0 && (
              <div style={{ background: "white", border: "1px solid rgba(185,167,146,.25)", borderRadius: 10, padding: "14px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <h4 style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: "var(--dark)" }}>
                    Individual Pieces in this Delivery ({lineTaggedItems.length})
                  </h4>
                  <span style={{ fontSize: 12, color: "var(--taupe)" }}>
                    Each piece has its own scannable barcode for counter billing
                  </span>
                </div>
                <div style={{ maxHeight: 180, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                    <thead>
                      <tr style={{ borderBottom: "1.5px solid rgba(185,167,146,.25)", color: "var(--taupe)", textAlign: "left" }}>
                        <th style={{ padding: "6px 8px" }}>Barcode / Item #</th>
                        <th style={{ padding: "6px 8px" }}>Gross Wt</th>
                        <th style={{ padding: "6px 8px" }}>Net Wt</th>
                        <th style={{ padding: "6px 8px" }}>Status</th>
                        <th style={{ padding: "6px 8px", textAlign: "right" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineTaggedItems.map((item) => (
                        <tr key={item.inventoryItemID} style={{ borderBottom: "1px solid rgba(185,167,146,.12)" }}>
                          <td style={{ padding: "6px 8px", fontWeight: 700, color: "var(--burgundy-dark)" }}>
                            #{item.inventoryItemID}
                          </td>
                          <td style={{ padding: "6px 8px" }}>{item.grossWeight ? `${parseFloat(item.grossWeight).toFixed(3)}g` : "—"}</td>
                          <td style={{ padding: "6px 8px", fontWeight: 600 }}>{item.netWeight ? `${parseFloat(item.netWeight).toFixed(3)}g` : "—"}</td>
                          <td style={{ padding: "6px 8px" }}>
                            <span style={{
                              fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
                              background: item.quantity > 0 ? "rgba(46,125,50,.1)" : "rgba(104,26,45,.1)",
                              color: item.quantity > 0 ? "#2e7d32" : "#9c4221"
                            }}>
                              {item.quantity > 0 ? "In Stock" : "Sold"}
                            </span>
                          </td>
                          <td style={{ padding: "6px 8px", textAlign: "right" }}>
                            <button
                              type="button"
                              className="rv-action-btn"
                              style={{ padding: "2px 8px", fontSize: 11.5 }}
                              title="Print this individual tag"
                              onClick={() => onPrintTag(item, lineProd)}
                            >
                              <PrintIcon /> Print
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Line comments */}
            <div>
              <h4 style={{ margin: "0 0 8px 0", fontSize: 14, fontWeight: 700, color: "var(--dark)" }}>
                Line Item Notes
              </h4>
              <div style={{
                padding: "12px 16px",
                background: "var(--pearl-bg, #fbf9f6)",
                border: "1px solid rgba(185,167,146,.2)",
                borderRadius: 10,
                fontSize: 13.5,
                color: line.comments ? "var(--dark)" : "var(--taupe)",
                fontStyle: line.comments ? "normal" : "italic",
                minHeight: 48
              }}>
                {line.comments || "No comments provided for this line item."}
              </div>
            </div>

            {/* Receipt Header Information */}
            <div>
              <h4 style={{ margin: "0 0 12px 0", fontSize: 14, fontWeight: 700, color: "var(--dark)" }}>
                Stock Receipt Header Information
              </h4>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 12,
                padding: "16px",
                background: "white",
                borderRadius: 10,
                border: "1px solid rgba(185,167,146,.2)"
              }}>
                <div>
                  <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 700, textTransform: "uppercase" }}>Vendor</span>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--dark)", marginTop: 2 }}>{vendorName}</div>
                </div>

                <div>
                  <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 700, textTransform: "uppercase" }}>Receipt Date</span>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--dark)", marginTop: 2 }}>{fmtDate(receipt.receiptDate)}</div>
                </div>

                <div>
                  <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 700, textTransform: "uppercase" }}>Receipt Reference</span>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--dark)", marginTop: 2 }}>Receipt #{receipt.receiptID}</div>
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 700, textTransform: "uppercase" }}>General Receipt Comments</span>
                  <div style={{ fontSize: 13, color: receipt.comments ? "var(--dark)" : "var(--taupe)", fontStyle: receipt.comments ? "normal" : "italic", marginTop: 4 }}>
                    {receipt.comments || "No general comments recorded."}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
}

/* ══════════════════════════════════════
   MAIN STOCK RECEIPT PAGE
══════════════════════════════════════ */
export default function StockReceipt() {
  const [receipts, setReceipts] = useState([]);
  const [details, setDetails] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [toast, setToast] = useState(null);

  const [tagModal, setTagModal] = useState(null);
  const [tagSaving, setTagSaving] = useState(false);
  const [printModal, setPrintModal] = useState(null);

  const tableRef = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  useResizableColumns(tableRef, tableReady, [50, 180, 240, 110, 95, 140, 110]);

  const fetchAll = async () => {
    try {
      setLoading(true); setTableReady(false);
      const [rData, dData, vData, pData, iData, sData] = await Promise.all([
        stockReceiptApi.getAll(),
        stockReceiptDtlApi.getAll(),
        vendorApi.getAll(),
        productApi.getAll(),
        inventoryItemDtlApi.getAll(),
        inventorySummaryApi.getAll(),
      ]);
      setReceipts(Array.isArray(rData) ? rData : rData?.data ?? []);
      setDetails(Array.isArray(dData) ? dData : dData?.data ?? []);
      setVendors(Array.isArray(vData) ? vData : vData?.data ?? []);
      setProducts(Array.isArray(pData) ? pData : pData?.data ?? []);
      setInventoryItems(Array.isArray(iData) ? iData : iData?.data ?? []);
      setStocks(Array.isArray(sData) ? sData : sData?.data ?? []);
    } catch (err) {
      showToast(err.message || "Failed to load stock receipts.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  /* ── Auto open New Receipt modal from Executive Short-Paths ── */
  useEffect(() => {
    if (!loading) {
      const autoModal = sessionStorage.getItem("reva_auto_modal");
      if (autoModal === "new-receipt") {
        sessionStorage.removeItem("reva_auto_modal");
        setModal({ data: {} });
      }
    }
  }, [loading]);

  useEffect(() => {
    const handleOpenModal = (e) => {
      if (e?.detail === "new-receipt") {
        sessionStorage.removeItem("reva_auto_modal");
        setModal({ data: {} });
      }
    };
    window.addEventListener("reva:open_modal", handleOpenModal);
    return () => window.removeEventListener("reva:open_modal", handleOpenModal);
  }, []);

  const showToast = (message, type = "success") => setToast({ message, type });
  const vendorMap = Object.fromEntries(vendors.map(v => [v.vendorID, v.vendorName]));

  /* ── Open edit modal with lines pre-loaded ── */
  const openEdit = (r) => {
    const existingLines = details.filter(d => d.receiptID === r.receiptID);
    setModal({ data: { ...r, lines: existingLines } });
  };

  /* ── Save ── */
  const handleSave = async ({ vendorID, receiptDate, comments, lines, autoTag }) => {
    if (saving) return;
    setSaving(true);
    try {
      let receiptID;

      if (modal?.data?.receiptID) {
        // Update header
        await stockReceiptApi.update({
          ...modal.data,
          vendorID: Number(vendorID),
          receiptDate,
          comments,
        });
        receiptID = modal.data.receiptID;

        // Update or create each line sequentially
        for (const l of lines) {
          if (l.receiptLineID) {
            await stockReceiptDtlApi.update({
              receiptLineID: l.receiptLineID,
              receiptID,
              productID: Number(l.productID),
              quantity: Number(l.quantity),
              unitWeight: parseFloat(l.unitWeight) || 0,
              totalWeight: parseFloat(l.totalWeight) || 0,
              comments: l.comments || "",
            });
          } else {
            await stockReceiptDtlApi.create({
              receiptID,
              productID: Number(l.productID),
              quantity: Number(l.quantity),
              unitWeight: parseFloat(l.unitWeight) || 0,
              totalWeight: parseFloat(l.totalWeight) || 0,
              comments: l.comments || "",
            });
          }
        }
        showToast("Delivery slip updated successfully.");

      } else {
        // Create header
        const created = await stockReceiptApi.create({ vendorID: Number(vendorID), receiptDate, comments });
        receiptID = created?.receiptID ?? created?.data?.receiptID;
        if (!receiptID) throw new Error("No receiptID returned from server.");

        try {
          // Create all lines sequentially
          const createdReceiptLines = [];
          for (const l of lines) {
            const res = await stockReceiptDtlApi.create({
              receiptID,
              productID: Number(l.productID),
              quantity: Number(l.quantity),
              unitWeight: parseFloat(l.unitWeight) || 0,
              totalWeight: parseFloat(l.totalWeight) || 0,
              comments: l.comments || "",
            });
            const lineObj = res?.data ?? res;
            createdReceiptLines.push({
              ...l,
              receiptLineID: lineObj?.receiptLineID || lineObj?.receiptLineId || lineObj?.id,
              receiptID,
            });
          }
          showToast("Delivery slip saved successfully.");

          // If autoTag was checked, auto-generate barcode tags for all pieces immediately
          if (autoTag && createdReceiptLines.length > 0) {
            const hasMissingLineId = createdReceiptLines.some(cl => !cl.receiptLineID);
            let targetLines = createdReceiptLines;
            if (hasMissingLineId) {
              try {
                const freshReceiptLinesRaw = await stockReceiptDtlApi.getAll();
                const freshReceiptLines = Array.isArray(freshReceiptLinesRaw) ? freshReceiptLinesRaw : freshReceiptLinesRaw?.data ?? [];
                targetLines = freshReceiptLines.filter(fl => Number(fl.receiptID) === Number(receiptID));
              } catch (_) { }
            }
            setModal(null);
            emitDataMutation("stock_receipt");
            emitDataMutation("stock");
            await fetchAll();
            if (targetLines.length > 0) {
              await handleAutoTagAll(targetLines);
            }
            return;
          }
        } catch (createErr) {
          // Silent rollback: clean up partial lines and orphaned receipt header to prevent 409 conflict on retry
          if (receiptID) {
            try {
              const freshLinesRaw = await stockReceiptDtlApi.getAll().catch(() => []);
              const freshLines = Array.isArray(freshLinesRaw) ? freshLinesRaw : freshLinesRaw?.data ?? [];
              const linesToDelete = freshLines.filter(fl => Number(fl.receiptID) === Number(receiptID));
              for (const line of linesToDelete) {
                const lineId = line.receiptLineID || line.receiptLineId || line.id;
                if (lineId) await stockReceiptDtlApi.remove(lineId).catch(() => {});
              }
              await stockReceiptApi.remove(receiptID).catch(() => {});
            } catch (_) {
              // Ignore secondary cleanup errors
            }
          }
          throw createErr;
        }
      }
      setModal(null);
      emitDataMutation("stock_receipt");
      fetchAll();
    } catch (err) {
      let msg = err.message || "Something went wrong.";
      if (msg.includes("conflicts with existing data") || msg.includes("Refresh and try again")) {
        msg = "The delivery could not be saved due to a stock ledger collision. Temporary data was cleaned up; please click 'Save Receipt' again.";
      }
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await stockReceiptApi.remove(confirm.id);
      showToast("Stock receipt deleted.");
      setConfirm(null);
      emitDataMutation("stock_receipt");
      fetchAll();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  };

  /* ── Tag Handlers (Option 1: Individual Piece Tags) ── */
  const handleSaveTag = async (tagDataOrPieces) => {
    setTagSaving(true);
    try {
      const pieceList = Array.isArray(tagDataOrPieces) ? tagDataOrPieces : [tagDataOrPieces];
      if (pieceList.length === 0) return;

      const freshStocksRaw = await inventorySummaryApi.getAll();
      const freshStocks = Array.isArray(freshStocksRaw) ? freshStocksRaw : freshStocksRaw?.data ?? [];
      setStocks(freshStocks); // keep local state in sync too

      const prodId = Number(pieceList[0].productID);
      const matchingStock = freshStocks.find(s => s.productID === prodId);
      if (!matchingStock?.stockID) {
        showToast(
          "Stock record not found for this product. Save the receipt line first, then generate the tag.",
          "error"
        );
        return;
      }

      const createdItems = [];
      for (const piece of pieceList) {
        const res = await inventoryItemDtlApi.create({
          receiptLineID: Number(piece.receiptLineID),
          productID:     Number(piece.productID),
          grossWeight:   parseFloat(piece.grossWeight) || 0,
          lessWeight:    parseFloat(piece.lessWeight)  || 0,
          netWeight:     parseFloat(piece.netWeight)   || 0,
          quantity:      1, // Each individual physical piece has Quantity = 1
          barcodeID:     0,
          stockID:       matchingStock.stockID,
        });
        let createdObj = res?.data ?? res;
        if (createdObj && createdObj.inventoryItemID) {
          try {
            const product = products.find(p => p.productID === Number(piece.productID));
            const newBarcode = await barcodeApi.create({
              inventoryItemID: createdObj.inventoryItemID,
              barcodeValue: String(createdObj.inventoryItemID),
              barcodeType: product?.barcodeType || "Code128",
              isActive: true,
            });
            if (newBarcode?.barcodeID || newBarcode?.data?.barcodeID) {
              const bId = newBarcode?.barcodeID || newBarcode?.data?.barcodeID;
              await inventoryItemDtlApi.update({
                ...createdObj,
                barcodeID: bId,
              });
              createdObj = { ...createdObj, barcodeID: bId };
            }
          } catch (e) {
            console.warn("Barcode creation non-fatal error:", e);
          }
          createdItems.push(createdObj);
        }
      }

      showToast(`Successfully created ${createdItems.length} jewelry inventory ${createdItems.length === 1 ? "tag" : "tags"}.`);
      setTagModal(null);
      emitDataMutation("stock");
      emitDataMutation("stock_receipt");
      await fetchAll();

      if (createdItems.length > 0) {
        setPrintModal({
          items: createdItems,
          item: createdItems[0],
          product: products.find(p => p.productID === prodId),
          products,
          vendors,
        });
      }
    } catch (err) {
      showToast(err.message || "Failed to generate item tag.", "error");
    } finally {
      setTagSaving(false);
    }
  };

  const handleAutoTagAll = async (lines) => {
    // Find lines where tagged pieces count is less than line quantity
    const linesToTag = lines.filter(l => {
      const lineTaggedCount = inventoryItems.filter(i => Number(i.receiptLineID) === Number(l.receiptLineID)).length;
      return lineTaggedCount < (Number(l.quantity) || 1);
    });

    if (linesToTag.length === 0) {
      showToast("All line items are already tagged.");
      return;
    }

    setSaving(true);
    try {
      // Fetch fresh stocks first — receipt lines already updated the summary server-side
      const freshStocksRaw = await inventorySummaryApi.getAll();
      const freshStocks = Array.isArray(freshStocksRaw) ? freshStocksRaw : freshStocksRaw?.data ?? [];
      setStocks(freshStocks);

      // Check all products have a stock row now
      const missing = linesToTag.find(l => !freshStocks.find(s => s.productID === Number(l.productID))?.stockID);
      if (missing) {
        showToast(
          "Stock record not found for one or more products. Ensure all receipt lines are saved first.",
          "error"
        );
        return;
      }

      const allCreated = [];
      for (const l of linesToTag) {
        const matchingStock = freshStocks.find(s => s.productID === Number(l.productID));
        const totalLineQty = Math.max(1, Number(l.quantity) || 1);
        const existingCount = inventoryItems.filter(i => Number(i.receiptLineID) === Number(l.receiptLineID)).length;
        const neededQty = totalLineQty - existingCount;

        const totalWeight = parseFloat(l.totalWeight) || 0;
        const unitWeight = l.unitWeight
          ? parseFloat(l.unitWeight)
          : totalWeight > 0
          ? parseFloat((totalWeight / totalLineQty).toFixed(3))
          : 0;

        let accumulatedWeight = 0;
        for (let q = 0; q < neededQty; q++) {
          const isLast = q === neededQty - 1;
          const pieceWeight = isLast && !l.unitWeight && totalWeight > 0
            ? parseFloat(Math.max(0, totalWeight - accumulatedWeight).toFixed(3))
            : unitWeight;
          accumulatedWeight += pieceWeight;

          const res = await inventoryItemDtlApi.create({
            receiptLineID: Number(l.receiptLineID),
            productID:     Number(l.productID),
            grossWeight:   pieceWeight,
            lessWeight:    0,
            netWeight:     pieceWeight,
            quantity:      1,
            barcodeID:     0,
            stockID:       matchingStock.stockID,
          });
          let createdObj = res?.data ?? res;
          if (createdObj && createdObj.inventoryItemID) {
            try {
              const product = products.find(p => p.productID === Number(l.productID));
              const newBarcode = await barcodeApi.create({
                inventoryItemID: createdObj.inventoryItemID,
                barcodeValue: String(createdObj.inventoryItemID),
                barcodeType: product?.barcodeType || "Code128",
                isActive: true,
              });
              if (newBarcode?.barcodeID || newBarcode?.data?.barcodeID) {
                const bId = newBarcode?.barcodeID || newBarcode?.data?.barcodeID;
                await inventoryItemDtlApi.update({
                  ...createdObj,
                  barcodeID: bId,
                });
                createdObj = { ...createdObj, barcodeID: bId };
              }
            } catch (e) {
              console.warn("Bulk barcode creation non-fatal error:", e);
            }
            allCreated.push(createdObj);
          }
        }
      }

      showToast(`Successfully created ${allCreated.length} jewelry inventory tags.`);
      emitDataMutation("stock");
      emitDataMutation("stock_receipt");
      await fetchAll();

      if (allCreated.length > 0) {
        setPrintModal({
          items: allCreated,
          item: allCreated[0],
          products,
          vendors,
        });
      }
    } catch (err) {
      showToast(err.message || "Failed to auto-tag consignment items.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handlePrintTag = (itemOrItems, product) => {
    if (Array.isArray(itemOrItems)) {
      setPrintModal({
        items: itemOrItems,
        item: itemOrItems[0],
        product: product || products.find(p => p.productID === itemOrItems[0]?.productID),
        products,
        vendors,
      });
    } else {
      setPrintModal({
        item: itemOrItems,
        product: product || products.find(p => p.productID === itemOrItems?.productID),
        products,
        vendors,
      });
    }
  };

  const handlePrintBatch = (items) => {
    setPrintModal({
      items,
      products,
      vendors,
    });
  };

  const fmt = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };
  const sortedReceipts = useMemo(() => {
    return [...receipts].sort((a, b) => {
      const timeA = new Date(a.receiptDate || a.createdAt || 0).getTime() || 0;
      const timeB = new Date(b.receiptDate || b.createdAt || 0).getTime() || 0;
      if (timeB !== timeA) return timeB - timeA;
      return Number(b.receiptID || 0) - Number(a.receiptID || 0);
    });
  }, [receipts]);

  const filtered = sortedReceipts.filter(r => {
    const q = search.toLowerCase();
    const rLines = details.filter(d => Number(d.receiptID) === Number(r.receiptID));
    const hasMatchingProduct = rLines.some(l => {
      const prod = products.find(p => p.productID === l.productID);
      return (prod?.productName ?? "").toLowerCase().includes(q) || (prod?.productCode ?? "").toLowerCase().includes(q);
    });
    return (
      String(r.receiptID).includes(q) ||
      (vendorMap[r.vendorID] ?? "").toLowerCase().includes(q) ||
      (r.comments ?? "").toLowerCase().includes(q) ||
      hasMatchingProduct
    );
  });

  const { paged, page, totalPages, pageSize, setPage, setPageSize, pageStart, pageEnd, totalItems } =
    usePagination(filtered, 10);

  if (drawer) {
    return (
      <div className="rv-inv-page">
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        {modal && (
          <StockReceiptModal
            initial={modal.data}
            vendors={vendors}
            products={products}
            onSave={handleSave}
            onClose={() => setModal(null)}
            saving={saving}
          />
        )}
        {tagModal && (
          <TagGenerationModal
            line={tagModal.line}
            product={tagModal.product}
            receipt={tagModal.receipt}
            vendorName={tagModal.vendorName}
            onSave={handleSaveTag}
            onClose={() => setTagModal(null)}
            saving={tagSaving}
          />
        )}
        {printModal && (
          <BarcodeLabelPrinter
            item={printModal.item}
            items={printModal.items}
            product={printModal.product}
            products={products}
            vendors={vendors}
            onClose={() => setPrintModal(null)}
          />
        )}
        <StockReceiptDetailView
          receipt={drawer}
          vendors={vendors}
          products={products}
          allDetails={details}
          inventoryItems={inventoryItems}
          onBack={() => {
            setDrawer(null);
            const el = document.querySelector(".rv-content");
            if (el) el.scrollTop = 0;
            window.scrollTo(0, 0);
          }}
          onEdit={openEdit}
          onPrintTag={handlePrintTag}
          onGenerateTag={(line, prod, r) => setTagModal({ line, product: prod, receipt: r, vendorName: vendorMap[r.vendorID] })}
          onPrintBatch={handlePrintBatch}
          onAutoTagAll={handleAutoTagAll}
        />
      </div>
    );
  }

  return (
    <div className="rv-inv-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {modal && (
        <StockReceiptModal
          initial={modal.data}
          vendors={vendors}
          products={products}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}
      {confirm && (
        <ConfirmDialog
          name={`Receipt #${confirm.id}`}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
      {tagModal && (
        <TagGenerationModal
          line={tagModal.line}
          product={tagModal.product}
          receipt={tagModal.receipt}
          vendorName={tagModal.vendorName}
          onSave={handleSaveTag}
          onClose={() => setTagModal(null)}
          saving={tagSaving}
        />
      )}
      {printModal && (
        <BarcodeLabelPrinter
          item={printModal.item}
          items={printModal.items}
          product={printModal.product}
          products={products}
          vendors={vendors}
          onClose={() => setPrintModal(null)}
        />
      )}

      {/* Topbar */}
      <div className="rv-inv-topbar">
        <div className="rv-inv-topbar-left" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <h2 className="rv-inv-heading">Deliveries</h2>
            <span className="rv-inv-count">{receipts.length} slips</span>
          </div>
          <p style={{ margin: 0, fontSize: "13px", color: "var(--taupe, #8a7a6b)" }}>
            Record incoming vendor delivery slips, assign per-piece weights, and generate scannable barcode tags.
          </p>
        </div>
        <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
          <PlusIcon /> Inward Delivery
        </button>
      </div>

      {/* Filters */}
      <div className="rv-inv-filters">
        <div className="rv-inv-search">
          <SearchIcon />
          <input
            placeholder="Search by receipt ID, vendor, or comments…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="rv-inv-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>
      </div>

      {/* Table card */}
      <div className="rv-card rv-inv-card">
        {loading ? (
          <div className="rv-inv-empty">
            <div className="rv-loader" />
            <p>Loading stock receipts…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rv-inv-empty">
            <div className="rv-empty-icon"><ReceiptIcon /></div>
            <p className="rv-empty-title">No stock receipts found</p>
            <p className="rv-empty-sub">
              {search ? "Try a different search term." : "Record your first vendor delivery to get started."}
            </p>
            {!search && (
              <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                <PlusIcon /> New Receipt
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="rv-desktop-table-view">
              <div className="rv-table-wrap rv-inv-table-wrap">
                <table className="rv-table rv-inv-table" ref={tableRef}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Vendor</th>
                      <th>Products / Items</th>
                      <th>Date</th>
                      <th>Total Qty</th>
                      <th>Barcode Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((r, i) => {
                      const receiptLines = details.filter(d => Number(d.receiptID) === Number(r.receiptID));
                      const receiptQty = receiptLines.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);
                      const receiptTaggedCount = inventoryItems.filter(item =>
                        receiptLines.some(l => Number(l.receiptLineID) === Number(item.receiptLineID))
                      ).length;
                      const isReceiptFullyTagged = receiptQty > 0 && receiptTaggedCount >= receiptQty;
                      const isReceiptPartiallyTagged = receiptTaggedCount > 0 && receiptTaggedCount < receiptQty;

                      return (
                        <tr
                          key={r.receiptID}
                          className="rv-inv-row"
                          onClick={() => setDrawer(r)}
                          style={{ cursor: "pointer" }}
                        >
                          <td className="rv-inv-idx">{pageStart + i}</td>
                          <td>
                            <span className="rv-inv-product-name">{vendorMap[r.vendorID] ?? `Vendor #${r.vendorID}`}</span>
                          </td>
                          <td>
                            {receiptLines.length === 0 ? (
                              <span className="rv-empty-val">—</span>
                            ) : (
                              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                {receiptLines.slice(0, 2).map((l, idx) => {
                                  const prod = products.find(p => p.productID === l.productID);
                                  const name = prod?.productName || `Product #${l.productID}`;
                                  const qty = l.quantity || 1;
                                  return (
                                    <span key={idx} style={{ fontSize: 12, color: "var(--dark)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      <strong style={{ color: "var(--burgundy-dark)" }}>{name}</strong> ({qty} {qty === 1 ? "pc" : "pcs"})
                                    </span>
                                  );
                                })}
                                {receiptLines.length > 2 && (
                                  <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 600 }}>
                                    +{receiptLines.length - 2} more item{receiptLines.length - 2 > 1 ? "s" : ""}
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                          <td>{fmt(r.receiptDate)}</td>
                          <td>
                            <span style={{ fontWeight: 600, color: "var(--dark)" }}>
                              {receiptQty} {receiptQty === 1 ? "pc" : "pcs"}
                            </span>
                          </td>
                          <td>
                            {receiptQty === 0 ? (
                              <span className="rv-empty-val">—</span>
                            ) : isReceiptFullyTagged ? (
                              <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#2e7d32",
                                background: "rgba(46, 125, 50, 0.1)",
                                padding: "3px 9px",
                                borderRadius: 12,
                                border: "1px solid rgba(46, 125, 50, 0.25)"
                              }}>
                                ✓ Fully Barcoded ({receiptTaggedCount}/{receiptQty})
                              </span>
                            ) : isReceiptPartiallyTagged ? (
                              <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#d97706",
                                background: "rgba(217, 119, 6, 0.1)",
                                padding: "3px 9px",
                                borderRadius: 12,
                                border: "1px solid rgba(217, 119, 6, 0.25)"
                              }}>
                                ⚡ Partial ({receiptTaggedCount}/{receiptQty})
                              </span>
                            ) : (
                              <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                                fontSize: 11,
                                fontWeight: 600,
                                color: "var(--taupe)",
                                background: "rgba(185, 167, 146, 0.12)",
                                padding: "3px 9px",
                                borderRadius: 12,
                                border: "1px solid rgba(185, 167, 146, 0.25)"
                              }}>
                                0/{receiptQty} Barcoded
                              </span>
                            )}
                          </td>
                          <td onClick={e => e.stopPropagation()}>
                            <div className="rv-actions">
                              <button
                                className="rv-action-btn"
                                title="View Details & Barcodes"
                                onClick={() => setDrawer(r)}
                                style={{ color: "var(--burgundy-dark)" }}
                              ><EyeIcon /></button>
                              {/* <button
                                className="rv-action-btn rv-action-btn--edit"
                                title="Edit"
                                onClick={() => openEdit(r)}
                              ><EditIcon /></button>
                              <button
                                className="rv-action-btn rv-action-btn--del"
                                title="Delete"
                                onClick={() => setConfirm({ id: r.receiptID })}
                              ><TrashIcon /></button> */}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Cards View */}
            <div className="rv-mobile-cards-view">
              <RvDataCardList
                loading={loading}
                items={paged}
                emptyIcon={<ReceiptIcon />}
                emptyTitle="No stock receipts found"
                emptySubtitle={search ? "Try a different search term." : "Record your first vendor delivery to get started."}
                emptyAction={
                  !search && (
                    <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
                      <PlusIcon /> New Receipt
                    </button>
                  )
                }
                renderCard={(r, i) => {
                  const receiptLines = details.filter(d => Number(d.receiptID) === Number(r.receiptID));
                  const receiptQty = receiptLines.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);
                  const receiptTaggedCount = inventoryItems.filter(item =>
                    receiptLines.some(l => Number(l.receiptLineID) === Number(item.receiptLineID))
                  ).length;
                  const isReceiptFullyTagged = receiptQty > 0 && receiptTaggedCount >= receiptQty;
                  const isReceiptPartiallyTagged = receiptTaggedCount > 0 && receiptTaggedCount < receiptQty;
                  const tagLabel = receiptQty === 0
                    ? "—"
                    : isReceiptFullyTagged
                    ? `✓ Barcoded (${receiptTaggedCount}/${receiptQty})`
                    : isReceiptPartiallyTagged
                    ? `⚡ Partial (${receiptTaggedCount}/${receiptQty})`
                    : `0/${receiptQty} Barcoded`;

                  return (
                    <RvDataCard
                      key={r.receiptID}
                      index={pageStart + i}
                      title={vendorMap[r.vendorID] ?? `Vendor #${r.vendorID}`}
                      subtitle={fmt(r.receiptDate)}
                      badge={null}
                      fields={[
                        {
                          label: "Items",
                          value: receiptLines.length === 0
                            ? "—"
                            : receiptLines.map(l => {
                                const prod = products.find(p => p.productID === l.productID);
                                return `${prod?.productName || `Product #${l.productID}`} (${l.quantity || 1})`;
                              }).join(", ")
                        },
                        { label: "Delivery Date", value: fmt(r.receiptDate) },
                        { label: "Total Qty", value: `${receiptQty} pcs` },
                        { label: "Barcode Status", value: tagLabel },
                      ]}
                      onClick={() => setDrawer(r)}
                      actions={
                        <div className="rv-actions" onClick={e => e.stopPropagation()}>
                          <button
                            className="rv-action-btn"
                            title="View Details & Barcodes"
                            onClick={() => setDrawer(r)}
                            style={{ color: "var(--burgundy-dark)" }}
                          ><EyeIcon /></button>
                          {/* <button
                            className="rv-action-btn rv-action-btn--edit"
                            title="Edit"
                            onClick={() => openEdit(r)}
                          ><EditIcon /></button>
                          <button
                            className="rv-action-btn rv-action-btn--del"
                            title="Delete"
                            onClick={() => setConfirm({ id: r.receiptID })}
                          ><TrashIcon /></button> */}
                        </div>
                      }
                    />
                  );
                }}
              />
            </div>

            {/* Shared Pagination */}
            <Pagination
              page={page} totalPages={totalPages} pageSize={pageSize}
              setPage={setPage} setPageSize={setPageSize}
              pageStart={pageStart} pageEnd={pageEnd} totalItems={totalItems}
            />
          </>
        )}
      </div>

      {!loading && filtered.length > 0 && (
        <p className="rv-resize-hint rv-desktop-table-view"><GripIcon /> Drag column borders to resize · Click a row to view details</p>
      )}
    </div>
  );
}

/* ── Icons ── */
function PlusIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>; }
function SearchIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>; }
function EditIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>; }
function TrashIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>; }
function CloseIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>; }
function CheckIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>; }
function AlertIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>; }
function GripIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ width: 13, height: 13 }}><circle cx="9" cy="5" r="1" fill="currentColor" /><circle cx="9" cy="12" r="1" fill="currentColor" /><circle cx="9" cy="19" r="1" fill="currentColor" /><circle cx="15" cy="5" r="1" fill="currentColor" /><circle cx="15" cy="12" r="1" fill="currentColor" /><circle cx="15" cy="19" r="1" fill="currentColor" /></svg>; }
function ReceiptIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><line x1="10" y1="9" x2="8" y2="9" /></svg>; }
function EyeIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>; }
function TagIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>; }
function PrintIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>; }
function SparklesIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" /></svg>; }
